import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { renderChatMarkdownSafe } from '../lib/html-safe';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  GraduationCap, Search, MapPin, Building2, Globe, Star, Users,
  BookOpen, Clock, DollarSign, ChevronRight, ArrowRight, Sparkles,
  MessageSquare, Send, Loader2, Bot, User, LogIn, UserPlus, X,
  FileText, Upload, CheckCircle, School, LogOut, Calendar, Award,
  Sun, Moon, Filter, ChevronDown, SlidersHorizontal, Laptop, Building,
  Wallet, ArrowLeft, Calculator, Palette, Briefcase, Monitor, Database,
  PenTool, GraduationCap as Education, Cpu, Coins, Heart, Hotel, Users2,
  Scale, TrendingUp, Megaphone, Tv, Stethoscope, Brain, FlaskConical,
  Plane, Music, Languages
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Subject icons mapping
const subjectIcons = {
  'Accounting': Calculator,
  'Art': Palette,
  'Business': Briefcase,
  'Computer': Monitor,
  'Data': Database,
  'Design': PenTool,
  'Education': GraduationCap,
  'Engineering': Cpu,
  'Finance': Coins,
  'Health': Heart,
  'Hospitality': Hotel,
  'Human Resource': Users2,
  'Law': Scale,
  'MBA': TrendingUp,
  'Management': Briefcase,
  'Marketing': Megaphone,
  'Media': Tv,
  'Nursing': Stethoscope,
  'Psychology': Brain,
  'Science': FlaskConical,
  'Tourism': Plane,
  'Music': Music,
  'Languages': Languages
};

// Country images - using high quality stock images
const countryData = [
  { id: 'uk', name: 'United Kingdom', code: 'GB', image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&h=300&fit=crop', programs: 124 },
  { id: 'usa', name: 'United States', code: 'US', image: 'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=400&h=300&fit=crop', programs: 0 },
  { id: 'canada', name: 'Canada', code: 'CA', image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?w=400&h=300&fit=crop', programs: 0 },
  { id: 'australia', name: 'Australia', code: 'AU', image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=400&h=300&fit=crop', programs: 0 },
  { id: 'germany', name: 'Germany', code: 'DE', image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=400&h=300&fit=crop', programs: 0 },
  { id: 'france', name: 'France', code: 'FR', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&h=300&fit=crop', programs: 0 }
];

// AI Chat Message Component
const ChatMessage = ({ message, isUser, isTyping, isDark }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
    <div className={`flex items-start gap-3 max-w-[80%] ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${
        isUser 
          ? 'bg-gradient-to-br from-amber-500 to-orange-600' 
          : 'bg-gradient-to-br from-amber-400 to-yellow-500'
      } shadow-lg`}>
        {isUser ? <User className="w-5 h-5 text-white" /> : <Bot className="w-5 h-5 text-white" />}
      </div>
      <div className={`rounded-2xl px-5 py-3 shadow-md ${
        isUser 
          ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white' 
          : isDark 
            ? 'bg-gray-800/90 text-gray-100 border border-amber-500/30' 
            : 'bg-white text-gray-800 border border-amber-200'
      }`}>
        {isTyping ? (
          <div className="flex gap-1.5 py-1">
            <span className="w-2.5 h-2.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2.5 h-2.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2.5 h-2.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        ) : (
          <div className="text-sm leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ 
            __html: renderChatMarkdownSafe(message)
          }} />
        )}
      </div>
    </div>
  </div>
);

// Program Card for Search Results
const ProgramResultCard = ({ program, onApply, isDark }) => (
  <Card className={`transition-all duration-300 hover:shadow-lg group ${
    isDark 
      ? 'bg-gray-800/50 border-gray-700 hover:border-amber-500/50' 
      : 'bg-white border-gray-200 hover:border-amber-400 hover:shadow-amber-100'
  }`}>
    <CardContent className="p-5">
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className={`text-lg font-semibold mb-1 group-hover:text-amber-600 transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {program.name}
            </h4>
            {program.university && (
              <p className={`text-sm flex items-center gap-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                <School className="w-4 h-4" />
                {program.university.name}
              </p>
            )}
          </div>
          <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white">
            {program.degree_type || 'Program'}
          </Badge>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {program.duration && (
            <Badge variant="outline" className={`${isDark ? 'text-gray-400 border-gray-600' : 'text-gray-600 border-gray-300'}`}>
              <Clock className="w-3 h-3 mr-1" />
              {program.duration}
            </Badge>
          )}
          {program.tuition_fee && (
            <Badge variant="outline" className={`${isDark ? 'text-gray-400 border-gray-600' : 'text-gray-600 border-gray-300'}`}>
              <DollarSign className="w-3 h-3 mr-1" />
              {program.tuition_fee}
            </Badge>
          )}
          {program.campus && (
            <Badge variant="outline" className={`${isDark ? 'text-gray-400 border-gray-600' : 'text-gray-600 border-gray-300'}`}>
              <Building2 className="w-3 h-3 mr-1" />
              {program.campus}
            </Badge>
          )}
          {program.university?.country && (
            <Badge variant="outline" className={`${isDark ? 'text-gray-400 border-gray-600' : 'text-gray-600 border-gray-300'}`}>
              <MapPin className="w-3 h-3 mr-1" />
              {program.university.country}
            </Badge>
          )}
        </div>
        
        <div className="flex justify-end mt-2">
          <Button 
            onClick={onApply}
            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all"
          >
            Apply Now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// University Card
const UniversityCard = ({ university, onClick, isDark }) => (
  <Card 
    className={`cursor-pointer group transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 ${
      isDark 
        ? 'bg-gray-800/50 border-gray-700 hover:border-amber-500/50 hover:shadow-[0_20px_50px_rgba(251,191,36,0.15)]'
        : 'bg-white border-gray-200 hover:border-amber-400 hover:shadow-[0_20px_50px_rgba(251,191,36,0.15)]'
    }`}
    onClick={onClick}
  >
    <CardContent className="p-6">
      <div className="flex items-start gap-4">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
          isDark ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20' : 'bg-gradient-to-br from-amber-100 to-orange-100'
        }`}>
          {university.logo_url ? (
            <img src={university.logo_url} alt={university.name} className="w-12 h-12 object-contain" />
          ) : (
            <School className={`w-8 h-8 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-lg mb-1 truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {university.name}
          </h3>
          <div className={`flex items-center gap-2 text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{university.city || university.country || 'United Kingdom'}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white">
              <BookOpen className="w-3 h-3 mr-1" />
              {university.program_count || 0} Programs
            </Badge>
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 ${
          isDark ? 'text-amber-400' : 'text-amber-600'
        }`} />
      </div>
    </CardContent>
  </Card>
);

// Main Component
const StudentPortalPage = ({ initialView }) => {
  const chatEndRef = useRef(null);
  
  // Theme
  const [isDark, setIsDark] = useState(false);
  
  // Auth
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [student, setStudent] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('student_portal_token'));
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [authForm, setAuthForm] = useState({ email: '', password: '', first_name: '', last_name: '', phone: '' });
  
  // Application Form State
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationStep, setApplicationStep] = useState(1);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    // Personal Information
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    country_of_residence: '',
    passport_number: '',
    address: '',
    city: '',
    postal_code: '',
    // Academic Background
    highest_qualification: '',
    institution_name: '',
    graduation_year: '',
    grade_percentage: '',
    field_of_study: '',
    // English Test Scores
    english_test_type: '',
    overall_score: '',
    listening: '',
    reading: '',
    writing: '',
    speaking: '',
    test_date: '',
    // Additional Info
    work_experience: '',
    personal_statement: '',
    intake_preference: ''
  });
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [applicationSubmitting, setApplicationSubmitting] = useState(false);
  
  // Data
  const [universities, setUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [filters, setFilters] = useState(null);
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Hero Filters
  const [heroFilters, setHeroFilters] = useState({ degreeType: '', location: '', subject: '' });
  
  // Search Page Filters
  const [searchFilters, setSearchFilters] = useState({
    studyLevel: [],
    destination: [],
    tuitionBudget: [],
    learningMode: []
  });
  
  // Subject Explorer
  const [subjectSearch, setSubjectSearch] = useState('');
  const [showAllSubjects, setShowAllSubjects] = useState(false);
  
  // View
  const [activeView, setActiveView] = useState(initialView || 'home'); // 'home', 'search', 'university', 'applications'
  
  // AI Chat
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hello! 👋 I'm **StudyBot**, your AI education counselor.\n\nI can help you:\n• Find the perfect program\n• Answer questions about eligibility & fees\n• Guide you through applications\n\nWhat would you like to know?" }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  useEffect(() => {
    if (token) checkAuth();
    fetchUniversities();
    fetchFilters();
    // If initialView is 'search', trigger a search with no filters to show all programs
    if (initialView === 'search') {
      handleSearch({});
    }
  }, []);
  
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/student-portal/me`, { headers: { Authorization: `Bearer ${token}` } });
      setStudent(response.data);
      setIsLoggedIn(true);
      fetchMyApplications();
    } catch {
      localStorage.removeItem('student_portal_token');
      setToken(null);
    }
  };
  
  const fetchUniversities = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/student-portal/universities`);
      setUniversities(response.data);
    } catch (error) {
      console.error('Failed to load universities:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFilters = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/student-portal/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  };
  
  const fetchUniversityDetail = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/student-portal/universities/${id}`);
      setSelectedUniversity(response.data);
      setActiveView('university');
    } catch {
      toast.error('Failed to load university details');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMyApplications = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE}/api/student-portal/my-applications`, { headers: { Authorization: `Bearer ${token}` } });
      setMyApplications(response.data);
    } catch (error) {
      console.error('Failed to load applications:', error);
    }
  };
  
  const handleSearch = async (customFilters = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const filtersToUse = customFilters || searchFilters;
      
      // Hero filters
      if (heroFilters.subject && heroFilters.subject !== 'all') params.append('subject', heroFilters.subject);
      if (heroFilters.degreeType && heroFilters.degreeType !== 'all') params.append('degree_type', heroFilters.degreeType);
      if (heroFilters.location && heroFilters.location !== 'all') params.append('location', heroFilters.location);
      
      // Checkbox filters - use first selected value for now (backend supports single value)
      if (filtersToUse.studyLevel?.length > 0) params.append('study_level', filtersToUse.studyLevel[0]);
      if (filtersToUse.learningMode?.length > 0) params.append('learning_mode', filtersToUse.learningMode[0]);
      if (filtersToUse.tuitionBudget?.length > 0) params.append('tuition_range', filtersToUse.tuitionBudget[0]);
      if (filtersToUse.destination?.length > 0) params.append('location', filtersToUse.destination[0]);
      
      const response = await axios.get(`${API_BASE}/api/student-portal/search?${params.toString()}`);
      setSearchResults(response.data);
      setActiveView('search');
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle checkbox filter change - immediately updates search
  const handleFilterCheckbox = (filterType, value, checked) => {
    setSearchFilters(prev => {
      const currentValues = prev[filterType] || [];
      let newValues;
      
      if (checked) {
        // Add value if not already present
        newValues = currentValues.includes(value) ? currentValues : [...currentValues, value];
      } else {
        // Remove value
        newValues = currentValues.filter(v => v !== value);
      }
      
      const newFilters = { ...prev, [filterType]: newValues };
      
      // Trigger search with new filters
      setTimeout(() => handleSearch(newFilters), 0);
      
      return newFilters;
    });
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    const emptyFilters = {
      studyLevel: [],
      destination: [],
      tuitionBudget: [],
      learningMode: []
    };
    setSearchFilters(emptyFilters);
    handleSearch(emptyFilters);
  };
  
  // Handle subject click - search programs by subject
  const handleSubjectClick = async (subjectName) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/student-portal/search?subject=${encodeURIComponent(subjectName)}`);
      setSearchResults(response.data);
      setActiveView('search');
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle country click - search programs by country
  const handleCountryClick = async (countryName) => {
    setLoading(true);
    try {
      // Find the country ID from filters
      const countryFilter = filters?.locations?.find(loc => 
        loc.name.toLowerCase().includes(countryName.toLowerCase())
      );
      const locationParam = countryFilter ? `location=${countryFilter.id}` : '';
      const response = await axios.get(`${API_BASE}/api/student-portal/search?${locationParam}`);
      setSearchResults(response.data);
      setActiveView('search');
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Apply Now click
  const handleApplyNow = async (program) => {
    if (!isLoggedIn) {
      setShowAuthDialog(true);
      toast.info('Please sign up or login to apply');
      return;
    }
    
    // Set selected program and pre-fill form with student data
    setSelectedProgram(program);
    setApplicationForm(prev => ({
      ...prev,
      first_name: student?.first_name || '',
      last_name: student?.last_name || '',
      email: student?.email || '',
      phone: student?.phone || ''
    }));
    
    // Fetch required documents based on program and student nationality
    try {
      const params = new URLSearchParams();
      if (program.university?.id) params.append('university_id', program.university.id);
      if (student?.nationality) params.append('nationality', student.nationality);
      if (program.degree_type) params.append('degree_type', program.degree_type);
      
      const response = await axios.get(`${API_BASE}/api/student-portal/required-documents?${params.toString()}`);
      
      // Process documents - they might come as array of strings or objects
      let docs = response.data.documents || [];
      
      // If it's the university_required_docs format, extract the required_documents array
      if (docs.length > 0 && docs[0].required_documents) {
        // Flatten all required documents from all universities
        const allDocs = new Set();
        docs.forEach(d => {
          (d.required_documents || []).forEach(docName => allDocs.add(docName));
        });
        docs = Array.from(allDocs).map((name, idx) => ({
          id: name.toLowerCase().replace(/\s+/g, '_'),
          name: name,
          required: true
        }));
      }
      
      // If docs don't have proper structure, format them
      docs = docs.map((doc, idx) => {
        if (typeof doc === 'string') {
          return { id: doc.toLowerCase().replace(/\s+/g, '_'), name: doc, required: true };
        }
        return {
          id: doc.id || doc.name?.toLowerCase().replace(/\s+/g, '_') || `doc_${idx}`,
          name: doc.name || doc,
          required: doc.required !== false
        };
      });
      
      setRequiredDocuments(docs.length > 0 ? docs : [
        { id: 'passport', name: 'Passport Copy', required: true },
        { id: 'academic_transcript', name: 'Academic Transcript', required: true },
        { id: 'degree_certificate', name: 'Degree Certificate', required: true },
        { id: 'english_test', name: 'English Test Score (IELTS/TOEFL)', required: true },
        { id: 'cv', name: 'CV/Resume', required: false },
        { id: 'sop', name: 'Statement of Purpose', required: false }
      ]);
    } catch (error) {
      console.error('Failed to fetch required documents:', error);
      // Set default documents
      setRequiredDocuments([
        { id: 'passport', name: 'Passport Copy', required: true },
        { id: 'academic_transcript', name: 'Academic Transcript', required: true },
        { id: 'degree_certificate', name: 'Degree Certificate', required: true },
        { id: 'english_test', name: 'English Test Score (IELTS/TOEFL)', required: true },
        { id: 'cv', name: 'CV/Resume', required: false },
        { id: 'sop', name: 'Statement of Purpose', required: false }
      ]);
    }
    
    setApplicationStep(1);
    setShowApplicationForm(true);
  };
  
  // Handle document upload
  const handleDocumentUpload = async (docId, file) => {
    if (!file) return;
    
    // Store file locally for preview
    setUploadedDocuments(prev => ({
      ...prev,
      [docId]: {
        file,
        name: file.name,
        size: file.size,
        status: 'ready'
      }
    }));
    toast.success(`${file.name} ready for upload`);
  };
  
  // Submit application
  const handleSubmitApplication = async () => {
    setApplicationSubmitting(true);
    try {
      const applicationData = {
        university_id: selectedProgram?.university?.id || '',
        program_id: selectedProgram?.id || '',
        degree_type: selectedProgram?.degree_type || '',
        intake_id: applicationForm.intake_preference || '',
        personal_info: {
          first_name: applicationForm.first_name,
          last_name: applicationForm.last_name,
          email: applicationForm.email,
          telephone_1: applicationForm.phone,
          date_of_birth: applicationForm.date_of_birth,
          gender: applicationForm.gender,
          nationality: applicationForm.nationality,
          country_living: applicationForm.country_of_residence,
          passport_number: applicationForm.passport_number,
          address: applicationForm.address,
          city: applicationForm.city,
          postal_code: applicationForm.postal_code
        },
        academic_info: {
          highest_qualification: applicationForm.highest_qualification,
          institution_name: applicationForm.institution_name,
          graduation_year: applicationForm.graduation_year,
          grade_percentage: applicationForm.grade_percentage,
          field_of_study: applicationForm.field_of_study,
          english_test_type: applicationForm.english_test_type,
          english_overall: applicationForm.overall_score,
          english_listening: applicationForm.listening,
          english_reading: applicationForm.reading,
          english_writing: applicationForm.writing,
          english_speaking: applicationForm.speaking,
          english_test_date: applicationForm.test_date
        },
        notes: applicationForm.personal_statement
      };
      
      const response = await axios.post(
        `${API_BASE}/api/student-portal/apply`,
        applicationData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        const appId = response.data.application_id;
        
        // Upload documents (non-blocking - don't fail the whole submission)
        const uploadPromises = [];
        for (const [docId, docData] of Object.entries(uploadedDocuments)) {
          if (docData && docData.file) {
            const formData = new FormData();
            formData.append('file', docData.file);
            formData.append('document_type', docId);
            
            uploadPromises.push(
              axios.post(
                `${API_BASE}/api/student-portal/applications/${appId}/documents`,
                formData,
                { 
                  headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                  }
                }
              ).catch(err => {
                console.error(`Failed to upload ${docId}:`, err);
                // Don't throw - continue with other uploads
              })
            );
          }
        }
        
        // Wait for all uploads but don't fail if some fail
        if (uploadPromises.length > 0) {
          await Promise.allSettled(uploadPromises);
        }
        
        toast.success('Application submitted successfully!');
        setShowApplicationForm(false);
        setApplicationStep(1);
        setUploadedDocuments({});
        // Reset form
        setApplicationForm({
          first_name: student?.first_name || '',
          last_name: student?.last_name || '',
          email: student?.email || '',
          phone: student?.phone || '',
          date_of_birth: '',
          gender: '',
          nationality: '',
          country_of_residence: '',
          passport_number: '',
          address: '',
          city: '',
          postal_code: '',
          highest_qualification: '',
          institution_name: '',
          graduation_year: '',
          grade_percentage: '',
          field_of_study: '',
          english_test_type: '',
          overall_score: '',
          listening: '',
          reading: '',
          writing: '',
          speaking: '',
          test_date: '',
          work_experience: '',
          personal_statement: '',
          intake_preference: ''
        });
      } else {
        // Application submission failed
        toast.error(response.data.message || response.data.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Application submission error:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to submit application';
      toast.error(errorMsg);
    } finally {
      setApplicationSubmitting(false);
    }
  };
  
  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const endpoint = authMode === 'login' ? '/api/student-portal/login' : '/api/student-portal/register';
      const payload = authMode === 'login' ? { email: authForm.email, password: authForm.password } : authForm;
      const response = await axios.post(`${API_BASE}${endpoint}`, payload);
      if (response.data.success) {
        localStorage.setItem('student_portal_token', response.data.token);
        setToken(response.data.token);
        setStudent(response.data.student);
        setIsLoggedIn(true);
        setShowAuthDialog(false);
        toast.success(authMode === 'login' ? 'Welcome back!' : 'Account created!');
        fetchMyApplications();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('student_portal_token');
    setToken(null);
    setStudent(null);
    setIsLoggedIn(false);
    toast.success('Logged out');
  };
  
  const handleAIChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/api/student-portal/ai-chat`, {
        message: userMessage, history: chatMessages.slice(-10), session_id: `session-${Date.now()}`
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (response.data.success) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.message }]);
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: "I apologize, but I'm having trouble. Please try again." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const totalPrograms = universities.reduce((sum, u) => sum + (u.program_count || 0), 0);

  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDark ? 'bg-gray-950' : 'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50'}`}>
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {isDark ? (
          <>
            <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[150px]" />
            <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-[150px]" />
          </>
        ) : (
          <>
            <div className="absolute top-0 left-0 w-full h-[600px] bg-gradient-to-br from-amber-200/40 via-orange-200/30 to-transparent blur-[100px]" />
            <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-gradient-to-tl from-yellow-200/30 via-amber-200/20 to-transparent blur-[120px]" />
          </>
        )}
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-colors duration-300 ${
        isDark ? 'bg-gray-950/90 border-gray-800' : 'bg-white/80 border-amber-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setActiveView('home'); setSelectedUniversity(null); setSearchResults(null); }}>
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`font-bold text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  StudyPortal<span className="text-amber-500">.ai</span>
                </h1>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-amber-600/70'}`}>Find Your Dream Program</p>
              </div>
            </div>
            
            {/* Nav */}
            <nav className="hidden md:flex items-center gap-1">
              <Button variant="ghost" onClick={() => { setActiveView('home'); setSelectedUniversity(null); }}
                className={`rounded-xl ${isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-700 hover:text-amber-700 hover:bg-amber-100'}`}>
                <School className="w-4 h-4 mr-2" /> Universities
              </Button>
              <Button variant="ghost" onClick={() => setShowAIChat(true)}
                className={`rounded-xl ${isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-700 hover:text-amber-700 hover:bg-amber-100'}`}>
                <Sparkles className="w-4 h-4 mr-2" /> AI Counselor
              </Button>
              {isLoggedIn && (
                <Button variant="ghost" onClick={() => setActiveView('applications')}
                  className={`rounded-xl ${isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-700 hover:text-amber-700 hover:bg-amber-100'}`}>
                  <FileText className="w-4 h-4 mr-2" /> My Applications
                </Button>
              )}
            </nav>
            
            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setIsDark(!isDark)}
                className={`rounded-xl ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-amber-700 hover:bg-amber-100'}`}>
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              
              {isLoggedIn ? (
                <div className="flex items-center gap-3">
                  <span className={`hidden md:block text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Hi, {student?.first_name}!</span>
                  <Button variant="outline" size="sm" onClick={handleLogout}
                    className={`rounded-xl ${isDark ? 'border-gray-700 text-gray-300' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}>
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </Button>
                </div>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={() => { setAuthMode('login'); setShowAuthDialog(true); }}
                    className={`rounded-xl ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Login</Button>
                  <Button size="sm" onClick={() => { setAuthMode('register'); setShowAuthDialog(true); }}
                    className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/25">
                    <UserPlus className="w-4 h-4 mr-2" /> Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10">
        {/* HOME VIEW */}
        {activeView === 'home' && (
          <>
            {/* Hero Section */}
            <section className="relative py-16 px-4 overflow-hidden">
              <div className="max-w-6xl mx-auto text-center">
                {/* Badge */}
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
                  isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-white border border-amber-300 shadow-sm'
                }`}>
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className={`text-sm font-medium ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>AI-Powered University Search</span>
                </div>
                
                {/* Heading */}
                <h1 className={`text-4xl md:text-6xl font-bold mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Find Your Perfect{' '}
                  <span className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 bg-clip-text text-transparent">
                    Study Program
                  </span>
                </h1>
                
                <p className={`text-lg md:text-xl mb-10 max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Search from {universities.length}+ universities and {totalPrograms}+ programs. Get AI-powered guidance for your education journey.
                </p>
                
                {/* Search Filters Box */}
                <div className={`max-w-4xl mx-auto rounded-3xl p-6 md:p-8 shadow-2xl ${
                  isDark ? 'bg-gray-900/80 border border-gray-800' : 'bg-white border border-amber-200'
                }`}>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    {/* Degree Type */}
                    <div className="text-left">
                      <Label className={`text-sm font-medium mb-2 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <GraduationCap className="w-4 h-4 inline mr-2" />Select Degree Type
                      </Label>
                      <Select value={heroFilters.degreeType} onValueChange={(v) => setHeroFilters(p => ({ ...p, degreeType: v }))}>
                        <SelectTrigger className={`rounded-xl h-12 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-amber-50 border-amber-200'}`}>
                          <SelectValue placeholder="All Degree Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Degree Types</SelectItem>
                          {filters?.degree_types?.map(dt => (
                            <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Location */}
                    <div className="text-left">
                      <Label className={`text-sm font-medium mb-2 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <MapPin className="w-4 h-4 inline mr-2" />Select Location
                      </Label>
                      <Select value={heroFilters.location} onValueChange={(v) => setHeroFilters(p => ({ ...p, location: v }))}>
                        <SelectTrigger className={`rounded-xl h-12 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-amber-50 border-amber-200'}`}>
                          <SelectValue placeholder="All Locations" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Locations</SelectItem>
                          {filters?.locations?.map(loc => (
                            <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Subject */}
                    <div className="text-left">
                      <Label className={`text-sm font-medium mb-2 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <BookOpen className="w-4 h-4 inline mr-2" />Select Subject
                      </Label>
                      <Select value={heroFilters.subject} onValueChange={(v) => setHeroFilters(p => ({ ...p, subject: v }))}>
                        <SelectTrigger className={`rounded-xl h-12 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-amber-50 border-amber-200'}`}>
                          <SelectValue placeholder="All Subjects" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Subjects</SelectItem>
                          {filters?.subjects?.map(sub => (
                            <SelectItem key={sub.id} value={sub.name}>{sub.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button onClick={handleSearch} className="w-full md:w-auto px-10 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-lg shadow-lg shadow-amber-500/30">
                    <Search className="w-5 h-5 mr-2" />
                    Search Programs
                  </Button>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mt-12">
                  {[
                    { icon: School, label: 'Universities', value: universities.length },
                    { icon: BookOpen, label: 'Programs', value: totalPrograms },
                    { icon: Users, label: 'Students', value: '5000+' },
                    { icon: Award, label: 'Success Rate', value: '95%' },
                  ].map((stat, idx) => (
                    <div key={idx} className={`p-4 rounded-2xl transition-transform duration-300 hover:scale-105 ${isDark ? 'bg-gray-900/50 border border-gray-800' : 'bg-white/70 border border-amber-200 shadow-sm'}`}>
                      <stat.icon className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                      <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat.value}</div>
                      <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            
            {/* Search by Country Section */}
            <section className={`py-16 ${isDark ? 'bg-gray-900/30' : 'bg-gradient-to-b from-white to-amber-50/30'}`}>
              <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-10">
                  <h2 className={`text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Globe className="inline w-8 h-8 mr-3 text-amber-500" />
                    Search by Country
                  </h2>
                  <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Explore top study destinations around the world
                  </p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {countryData.map((country, idx) => (
                    <button
                      key={country.id}
                      onClick={() => handleCountryClick(country.name)}
                      className={`group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                        isDark ? 'bg-gray-800' : 'bg-white shadow-md'
                      }`}
                      style={{ animationDelay: `${idx * 100}ms` }}
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img 
                          src={country.image} 
                          alt={country.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                        <h3 className="font-semibold text-sm truncate">{country.name}</h3>
                        {country.programs > 0 && (
                          <p className="text-xs text-amber-300">{country.programs} programs</p>
                        )}
                      </div>
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        isDark ? 'bg-amber-500 text-black' : 'bg-amber-500 text-white'
                      }`}>
                        <MapPin className="w-3 h-3" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>
            
            {/* Featured Universities */}
            <section className="py-16 max-w-7xl mx-auto px-4">
              <div className="text-center mb-10">
                <h2 className={`text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <Building2 className="inline w-8 h-8 mr-3 text-amber-500" />
                  Featured Universities
                </h2>
                <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Partner institutions offering world-class education
                </p>
              </div>
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {universities.map((uni, idx) => (
                    <div
                      key={uni.id}
                      onClick={() => fetchUniversityDetail(uni.id)}
                      className={`group cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                        isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-amber-100 shadow-md'
                      }`}
                      style={{ animationDelay: `${idx * 150}ms` }}
                    >
                      <div className="flex">
                        {/* University Logo Placeholder */}
                        <div className={`w-32 h-32 flex-shrink-0 flex items-center justify-center ${
                          isDark ? 'bg-gradient-to-br from-amber-600/20 to-orange-600/20' : 'bg-gradient-to-br from-amber-100 to-orange-100'
                        }`}>
                          <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                            isDark ? 'bg-amber-500/30' : 'bg-white shadow-md'
                          }`}>
                            <School className="w-8 h-8 text-amber-600" />
                          </div>
                        </div>
                        <div className="flex-1 p-5">
                          <h3 className={`text-lg font-bold mb-2 group-hover:text-amber-600 transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {uni.name}
                          </h3>
                          <div className="flex items-center gap-4 mb-3">
                            <span className={`text-sm flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              <MapPin className="w-4 h-4 mr-1 text-amber-500" />
                              {uni.city || 'United Kingdom'}
                            </span>
                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                              {uni.programCount || 0} Programs
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex gap-1">
                              {[1,2,3,4,5].map(star => (
                                <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" />
                              ))}
                            </div>
                            <ChevronRight className={`w-5 h-5 transition-transform duration-300 group-hover:translate-x-2 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
            
            {/* Explore Subjects Section */}
            <section className={`py-16 ${isDark ? 'bg-gray-900/50' : 'bg-gradient-to-b from-amber-50/50 to-white'}`}>
              <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-10">
                  <h2 className={`text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <BookOpen className="inline w-8 h-8 mr-3 text-amber-500" />
                    Explore Subjects
                  </h2>
                  <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Discover subjects that match your interests and goals
                  </p>
                </div>
                
                {/* Subject Search Box */}
                <div className="max-w-md mx-auto mb-8">
                  <div className="relative">
                    <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-amber-500'}`} />
                    <Input
                      type="text"
                      placeholder="Search subjects..."
                      value={subjectSearch}
                      onChange={(e) => setSubjectSearch(e.target.value)}
                      className={`pl-12 pr-4 py-3 rounded-xl text-base ${
                        isDark 
                          ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' 
                          : 'bg-white border-amber-200 text-gray-900 placeholder:text-gray-400 focus:border-amber-400 shadow-sm'
                      }`}
                    />
                  </div>
                </div>
                
                {/* Subject Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {filters?.subjects
                    ?.filter(sub => sub.name.toLowerCase().includes(subjectSearch.toLowerCase()))
                    .slice(0, showAllSubjects ? undefined : 8)
                    .map((subject, idx) => {
                      const IconComponent = subjectIcons[subject.name] || BookOpen;
                      return (
                        <button
                          key={subject.id || idx}
                          onClick={() => handleSubjectClick(subject.name)}
                          className={`group p-5 rounded-xl text-left transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                            isDark 
                              ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-amber-500/50' 
                              : 'bg-white hover:bg-amber-50 border border-amber-100 hover:border-amber-300 shadow-sm'
                          }`}
                          style={{ animationDelay: `${idx * 50}ms` }}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                              isDark 
                                ? 'bg-amber-500/20 group-hover:bg-amber-500/30' 
                                : 'bg-amber-100 group-hover:bg-amber-200'
                            }`}>
                              <IconComponent className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                            </div>
                            <span className={`font-medium flex-1 ${
                              isDark 
                                ? 'text-white group-hover:text-amber-300' 
                                : 'text-gray-800 group-hover:text-amber-700'
                            }`}>
                              {subject.name}
                            </span>
                            <ChevronRight className={`w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 ${
                              isDark ? 'text-gray-600 group-hover:text-amber-400' : 'text-amber-400 group-hover:text-amber-600'
                            }`} />
                          </div>
                        </button>
                      );
                    })}
                </div>
                
                {/* See More Button */}
                {filters?.subjects?.length > 8 && !subjectSearch && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={() => setShowAllSubjects(!showAllSubjects)}
                      className={`inline-flex items-center px-8 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
                        isDark 
                          ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' 
                          : 'bg-amber-500 text-white hover:bg-amber-600 shadow-lg hover:shadow-xl'
                      }`}
                    >
                      {showAllSubjects ? 'Show less' : 'View all subjects'} 
                      <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-300 ${showAllSubjects ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {/* SEARCH RESULTS VIEW */}
        {activeView === 'search' && searchResults && (
          <section className="max-w-7xl mx-auto px-4 py-8">
            <Button variant="ghost" onClick={() => { setActiveView('home'); setSearchResults(null); }}
              className={`mb-6 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
            
            <div className="flex gap-8">
              {/* Sidebar Filters */}
              <aside className={`hidden lg:block w-72 flex-shrink-0 p-6 rounded-2xl h-fit sticky top-24 ${
                isDark ? 'bg-gray-900/50 border border-gray-800' : 'bg-white border border-amber-200 shadow-sm'
              }`}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`font-bold text-lg flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <SlidersHorizontal className="w-5 h-5 mr-2 text-amber-500" /> Filters
                  </h3>
                  {(searchFilters.studyLevel.length > 0 || searchFilters.destination.length > 0 || 
                    searchFilters.tuitionBudget.length > 0 || searchFilters.learningMode.length > 0) && (
                    <button 
                      onClick={clearAllFilters}
                      className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${
                        isDark ? 'text-amber-400 hover:bg-amber-500/20' : 'text-amber-600 hover:bg-amber-100'
                      }`}
                    >
                      Clear all
                    </button>
                  )}
                </div>
                
                {/* Study Level Checkboxes */}
                <div className="mb-6">
                  <Label className={`text-sm font-semibold mb-3 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <GraduationCap className="w-4 h-4 inline mr-2 text-amber-500" />Study Level
                  </Label>
                  <div className="space-y-2">
                    {filters?.study_levels?.map(sl => (
                      <label 
                        key={sl.id} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                          searchFilters.studyLevel.includes(sl.id)
                            ? isDark ? 'bg-amber-500/20' : 'bg-amber-100'
                            : isDark ? 'hover:bg-gray-800' : 'hover:bg-amber-50'
                        }`}
                      >
                        <Checkbox 
                          checked={searchFilters.studyLevel.includes(sl.id)}
                          onCheckedChange={(checked) => handleFilterCheckbox('studyLevel', sl.id, checked)}
                          className={`${isDark ? 'border-gray-600 data-[state=checked]:bg-amber-500' : 'border-amber-300 data-[state=checked]:bg-amber-500'}`}
                        />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{sl.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Study Destination Checkboxes */}
                <div className="mb-6">
                  <Label className={`text-sm font-semibold mb-3 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Globe className="w-4 h-4 inline mr-2 text-amber-500" />Study Destination
                  </Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {filters?.locations?.slice(0, 6).map(loc => (
                      <label 
                        key={loc.id} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                          searchFilters.destination.includes(loc.id)
                            ? isDark ? 'bg-amber-500/20' : 'bg-amber-100'
                            : isDark ? 'hover:bg-gray-800' : 'hover:bg-amber-50'
                        }`}
                      >
                        <Checkbox 
                          checked={searchFilters.destination.includes(loc.id)}
                          onCheckedChange={(checked) => handleFilterCheckbox('destination', loc.id, checked)}
                          className={`${isDark ? 'border-gray-600 data-[state=checked]:bg-amber-500' : 'border-amber-300 data-[state=checked]:bg-amber-500'}`}
                        />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{loc.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Tuition Budget Checkboxes */}
                <div className="mb-6">
                  <Label className={`text-sm font-semibold mb-3 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Wallet className="w-4 h-4 inline mr-2 text-amber-500" />Annual Tuition Budget
                  </Label>
                  <div className="space-y-2">
                    {filters?.tuition_ranges?.map(tr => (
                      <label 
                        key={tr.id} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                          searchFilters.tuitionBudget.includes(tr.id)
                            ? isDark ? 'bg-amber-500/20' : 'bg-amber-100'
                            : isDark ? 'hover:bg-gray-800' : 'hover:bg-amber-50'
                        }`}
                      >
                        <Checkbox 
                          checked={searchFilters.tuitionBudget.includes(tr.id)}
                          onCheckedChange={(checked) => handleFilterCheckbox('tuitionBudget', tr.id, checked)}
                          className={`${isDark ? 'border-gray-600 data-[state=checked]:bg-amber-500' : 'border-amber-300 data-[state=checked]:bg-amber-500'}`}
                        />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{tr.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Learning Mode Checkboxes */}
                <div className="mb-6">
                  <Label className={`text-sm font-semibold mb-3 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Laptop className="w-4 h-4 inline mr-2 text-amber-500" />Learning Mode
                  </Label>
                  <div className="space-y-2">
                    {filters?.learning_modes?.map(lm => (
                      <label 
                        key={lm.id} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                          searchFilters.learningMode.includes(lm.id)
                            ? isDark ? 'bg-amber-500/20' : 'bg-amber-100'
                            : isDark ? 'hover:bg-gray-800' : 'hover:bg-amber-50'
                        }`}
                      >
                        <Checkbox 
                          checked={searchFilters.learningMode.includes(lm.id)}
                          onCheckedChange={(checked) => handleFilterCheckbox('learningMode', lm.id, checked)}
                          className={`${isDark ? 'border-gray-600 data-[state=checked]:bg-amber-500' : 'border-amber-300 data-[state=checked]:bg-amber-500'}`}
                        />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{lm.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </aside>
              
              {/* Results */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Search Results
                    <span className={`ml-3 text-base font-normal ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      ({searchResults.total} programs found)
                    </span>
                  </h2>
                </div>
                
                {loading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
                ) : searchResults.programs?.length > 0 ? (
                  <div className="grid gap-4">
                    {searchResults.programs.map(prog => (
                      <ProgramResultCard key={prog.id} program={prog} isDark={isDark} onApply={() => handleApplyNow(prog)} />
                    ))}
                  </div>
                ) : (
                  <Card className={`${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-amber-200'}`}>
                    <CardContent className="py-16 text-center">
                      <Search className="w-16 h-16 mx-auto mb-4 text-amber-300" />
                      <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>No programs found. Try adjusting your filters.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </section>
        )}

        {/* UNIVERSITY DETAIL VIEW */}
        {activeView === 'university' && selectedUniversity && (
          <section className="max-w-7xl mx-auto px-4 py-8">
            <Button variant="ghost" onClick={() => { setActiveView('home'); setSelectedUniversity(null); }}
              className={`mb-6 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Universities
            </Button>
            
            {/* University Header */}
            <Card className={`mb-8 overflow-hidden ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-amber-200 shadow-xl'}`}>
              <div className={`h-32 bg-gradient-to-r ${isDark ? 'from-amber-900/30 to-orange-900/30' : 'from-amber-200 to-orange-200'}`} />
              <CardContent className="p-8 -mt-16">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className={`w-24 h-24 rounded-2xl flex items-center justify-center shadow-xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-amber-200'}`}>
                    <School className={`w-12 h-12 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                  </div>
                  <div className="flex-1">
                    <h2 className={`text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedUniversity.name}</h2>
                    <div className={`flex flex-wrap gap-4 mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      <span className="flex items-center gap-2"><MapPin className="w-4 h-4" />{selectedUniversity.city}, {selectedUniversity.country}</span>
                    </div>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{selectedUniversity.description || 'A prestigious institution.'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Programs */}
            <h3 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Available Programs ({selectedUniversity.programs?.length || 0})
            </h3>
            {selectedUniversity.programs?.length > 0 ? (
              <div className="grid gap-4">
                {selectedUniversity.programs.map((prog, idx) => (
                  <ProgramResultCard key={idx} program={{ ...prog, university: { name: selectedUniversity.name, country: selectedUniversity.country, id: selectedUniversity.id } }} isDark={isDark} onApply={() => handleApplyNow({ ...prog, university: { name: selectedUniversity.name, country: selectedUniversity.country, id: selectedUniversity.id } })} />
                ))}
              </div>
            ) : (
              <Card className={isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-amber-200'}>
                <CardContent className="py-12 text-center">
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Programs coming soon.</p>
                </CardContent>
              </Card>
            )}
          </section>
        )}

        {/* APPLICATIONS VIEW */}
        {activeView === 'applications' && isLoggedIn && (
          <section className="max-w-4xl mx-auto px-4 py-8">
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>My Applications</h2>
            {myApplications.length > 0 ? (
              <div className="grid gap-4">
                {myApplications.map(app => (
                  <Card key={app.id} className={isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-amber-200 shadow-md'}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{app.program_name || 'Program'}</h4>
                          <p className="text-amber-500">{app.university_name || 'University'}</p>
                          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Applied {new Date(app.created_at).toLocaleDateString()}</p>
                        </div>
                        <Badge className={app.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}>
                          {app.status?.toUpperCase() || 'PENDING'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className={isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-amber-200'}>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-amber-300" />
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No applications yet.</p>
                  <Button onClick={() => setActiveView('home')} className="mt-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white">
                    Explore Programs
                  </Button>
                </CardContent>
              </Card>
            )}
          </section>
        )}
      </main>

      {/* AI Chat Button */}
      {!showAIChat && (
        <button onClick={() => setShowAIChat(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/30 hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center">
          <MessageSquare className="w-6 h-6" />
        </button>
      )}

      {/* AI Chat Panel */}
      {showAIChat && (
        <div className={`fixed bottom-6 right-6 z-50 w-[400px] h-[550px] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${
          isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-amber-200'
        }`}>
          <div className={`p-4 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-amber-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>StudyBot</h3>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-amber-600'}`}>AI Education Counselor</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setShowAIChat(false)} className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <ScrollArea className="flex-1 p-4">
            {chatMessages.map((msg, idx) => <ChatMessage key={idx} message={msg.content} isUser={msg.role === 'user'} isDark={isDark} />)}
            {chatLoading && <ChatMessage message="" isUser={false} isTyping={true} isDark={isDark} />}
            <div ref={chatEndRef} />
          </ScrollArea>
          <div className={`p-4 border-t ${isDark ? 'border-gray-800' : 'border-amber-200'}`}>
            <div className={`flex items-center gap-2 rounded-xl px-4 py-2 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-amber-50 border border-amber-200'}`}>
              <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAIChat()}
                placeholder="Ask me anything..." className={`flex-1 bg-transparent outline-none text-sm ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`} />
              <Button size="icon" onClick={handleAIChat} disabled={chatLoading || !chatInput.trim()}
                className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 text-white">
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-gray-900 border-gray-800 text-white' : 'bg-white border-amber-200'}`}>
          <DialogHeader>
            <DialogTitle className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {authMode === 'login' ? 'Welcome Back!' : 'Create Account'}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              {authMode === 'login' ? 'Login to track applications' : 'Start your education journey'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAuth} className="space-y-4 mt-4">
            {authMode === 'register' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>First Name</Label>
                  <Input value={authForm.first_name} onChange={(e) => setAuthForm(p => ({ ...p, first_name: e.target.value }))} required
                    className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                </div>
                <div>
                  <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Last Name</Label>
                  <Input value={authForm.last_name} onChange={(e) => setAuthForm(p => ({ ...p, last_name: e.target.value }))} required
                    className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                </div>
              </div>
            )}
            <div>
              <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Email</Label>
              <Input type="email" value={authForm.email} onChange={(e) => setAuthForm(p => ({ ...p, email: e.target.value }))} required
                className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
            </div>
            <div>
              <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Password</Label>
              <Input type="password" value={authForm.password} onChange={(e) => setAuthForm(p => ({ ...p, password: e.target.value }))} required
                className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
            </div>
            {authMode === 'register' && (
              <div>
                <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Phone</Label>
                <Input value={authForm.phone} onChange={(e) => setAuthForm(p => ({ ...p, phone: e.target.value }))}
                  className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
              </div>
            )}
            <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white" disabled={authLoading}>
              {authLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {authMode === 'login' ? 'Login' : 'Create Account'}
            </Button>
          </form>
          <div className="text-center mt-4">
            <button onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
              className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
              {authMode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Login'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Application Form Dialog */}
      <Dialog open={showApplicationForm} onOpenChange={setShowApplicationForm}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-gray-900 border-gray-800 text-white' : 'bg-white border-amber-200'}`}>
          <DialogHeader>
            <DialogTitle className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Apply to {selectedProgram?.name}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              {selectedProgram?.university?.name} • {selectedProgram?.degree_type}
            </DialogDescription>
          </DialogHeader>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 my-6">
            {['Personal Info', 'Academic', 'English Test', 'Documents'].map((step, idx) => (
              <div key={idx} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  applicationStep > idx + 1
                    ? 'bg-green-500 text-white'
                    : applicationStep === idx + 1
                      ? 'bg-amber-500 text-white'
                      : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                }`}>
                  {applicationStep > idx + 1 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                </div>
                <span className={`ml-2 text-xs hidden sm:inline ${applicationStep === idx + 1 ? (isDark ? 'text-amber-400' : 'text-amber-600') : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
                  {step}
                </span>
                {idx < 3 && <ChevronRight className={`w-4 h-4 mx-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />}
              </div>
            ))}
          </div>
          
          <div className="space-y-6">
            {/* Step 1: Personal Information */}
            {applicationStep === 1 && (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <User className="w-5 h-5 mr-2 text-amber-500" /> Personal Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>First Name *</Label>
                    <Input value={applicationForm.first_name} onChange={(e) => setApplicationForm(p => ({ ...p, first_name: e.target.value }))}
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Last Name *</Label>
                    <Input value={applicationForm.last_name} onChange={(e) => setApplicationForm(p => ({ ...p, last_name: e.target.value }))}
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Email *</Label>
                    <Input type="email" value={applicationForm.email} onChange={(e) => setApplicationForm(p => ({ ...p, email: e.target.value }))}
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Phone *</Label>
                    <Input value={applicationForm.phone} onChange={(e) => setApplicationForm(p => ({ ...p, phone: e.target.value }))}
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Date of Birth *</Label>
                    <Input type="date" value={applicationForm.date_of_birth} onChange={(e) => setApplicationForm(p => ({ ...p, date_of_birth: e.target.value }))}
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Gender *</Label>
                    <Select value={applicationForm.gender} onValueChange={(v) => setApplicationForm(p => ({ ...p, gender: v }))}>
                      <SelectTrigger className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'}>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Nationality *</Label>
                    <Select value={applicationForm.nationality} onValueChange={(v) => setApplicationForm(p => ({ ...p, nationality: v }))}>
                      <SelectTrigger className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'}>
                        <SelectValue placeholder="Select nationality" />
                      </SelectTrigger>
                      <SelectContent>
                        {filters?.locations?.map(loc => (
                          <SelectItem key={loc.id} value={loc.name}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Country of Residence</Label>
                    <Select value={applicationForm.country_of_residence} onValueChange={(v) => setApplicationForm(p => ({ ...p, country_of_residence: v }))}>
                      <SelectTrigger className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'}>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {filters?.locations?.map(loc => (
                          <SelectItem key={loc.id} value={loc.name}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Passport Number</Label>
                    <Input value={applicationForm.passport_number} onChange={(e) => setApplicationForm(p => ({ ...p, passport_number: e.target.value }))}
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>City</Label>
                    <Input value={applicationForm.city} onChange={(e) => setApplicationForm(p => ({ ...p, city: e.target.value }))}
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                </div>
                <div>
                  <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Address</Label>
                  <Input value={applicationForm.address} onChange={(e) => setApplicationForm(p => ({ ...p, address: e.target.value }))}
                    className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                </div>
              </div>
            )}
            
            {/* Step 2: Academic Background */}
            {applicationStep === 2 && (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <GraduationCap className="w-5 h-5 mr-2 text-amber-500" /> Academic Background
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Highest Qualification *</Label>
                    <Select value={applicationForm.highest_qualification} onValueChange={(v) => setApplicationForm(p => ({ ...p, highest_qualification: v }))}>
                      <SelectTrigger className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'}>
                        <SelectValue placeholder="Select qualification" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high_school">High School / Secondary</SelectItem>
                        <SelectItem value="diploma">Diploma</SelectItem>
                        <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                        <SelectItem value="masters">Master's Degree</SelectItem>
                        <SelectItem value="phd">PhD / Doctorate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Field of Study *</Label>
                    <Input value={applicationForm.field_of_study} onChange={(e) => setApplicationForm(p => ({ ...p, field_of_study: e.target.value }))}
                      placeholder="e.g., Computer Science"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Institution Name *</Label>
                    <Input value={applicationForm.institution_name} onChange={(e) => setApplicationForm(p => ({ ...p, institution_name: e.target.value }))}
                      placeholder="University / College name"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Graduation Year *</Label>
                    <Input type="number" min="1990" max="2030" value={applicationForm.graduation_year} onChange={(e) => setApplicationForm(p => ({ ...p, graduation_year: e.target.value }))}
                      placeholder="e.g., 2023"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Grade / Percentage / GPA *</Label>
                    <Input value={applicationForm.grade_percentage} onChange={(e) => setApplicationForm(p => ({ ...p, grade_percentage: e.target.value }))}
                      placeholder="e.g., 85% or 3.5 GPA"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} required />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Work Experience (Years)</Label>
                    <Input type="number" min="0" max="50" value={applicationForm.work_experience} onChange={(e) => setApplicationForm(p => ({ ...p, work_experience: e.target.value }))}
                      placeholder="0"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: English Test Scores */}
            {applicationStep === 3 && (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <BookOpen className="w-5 h-5 mr-2 text-amber-500" /> English Proficiency Test
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Test Type *</Label>
                    <Select value={applicationForm.english_test_type} onValueChange={(v) => setApplicationForm(p => ({ ...p, english_test_type: v }))}>
                      <SelectTrigger className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'}>
                        <SelectValue placeholder="Select test type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ielts">IELTS</SelectItem>
                        <SelectItem value="toefl">TOEFL</SelectItem>
                        <SelectItem value="pte">PTE Academic</SelectItem>
                        <SelectItem value="duolingo">Duolingo</SelectItem>
                        <SelectItem value="none">Not taken yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Overall Score</Label>
                    <Input value={applicationForm.overall_score} onChange={(e) => setApplicationForm(p => ({ ...p, overall_score: e.target.value }))}
                      placeholder="e.g., 7.0"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Listening</Label>
                    <Input value={applicationForm.listening} onChange={(e) => setApplicationForm(p => ({ ...p, listening: e.target.value }))}
                      placeholder="e.g., 7.5"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Reading</Label>
                    <Input value={applicationForm.reading} onChange={(e) => setApplicationForm(p => ({ ...p, reading: e.target.value }))}
                      placeholder="e.g., 7.0"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Writing</Label>
                    <Input value={applicationForm.writing} onChange={(e) => setApplicationForm(p => ({ ...p, writing: e.target.value }))}
                      placeholder="e.g., 6.5"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Speaking</Label>
                    <Input value={applicationForm.speaking} onChange={(e) => setApplicationForm(p => ({ ...p, speaking: e.target.value }))}
                      placeholder="e.g., 7.0"
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Test Date</Label>
                    <Input type="date" value={applicationForm.test_date} onChange={(e) => setApplicationForm(p => ({ ...p, test_date: e.target.value }))}
                      className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'} />
                  </div>
                </div>
                <div>
                  <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Personal Statement / Motivation</Label>
                  <textarea
                    value={applicationForm.personal_statement}
                    onChange={(e) => setApplicationForm(p => ({ ...p, personal_statement: e.target.value }))}
                    placeholder="Why do you want to study this program? What are your career goals?"
                    rows={4}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-amber-50 border-amber-200 text-gray-900'}`}
                  />
                </div>
              </div>
            )}
            
            {/* Step 4: Documents */}
            {applicationStep === 4 && (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <FileText className="w-5 h-5 mr-2 text-amber-500" /> Required Documents
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Upload the following documents to complete your application. Documents are filtered based on your nationality ({applicationForm.nationality || 'Not specified'}) and degree type ({selectedProgram?.degree_type || 'N/A'}).
                </p>
                <div className="grid gap-3">
                  {requiredDocuments.map((doc, idx) => (
                    <div key={doc.id || idx} className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            uploadedDocuments[doc.id || doc.name]
                              ? 'bg-green-100 text-green-600'
                              : isDark ? 'bg-gray-700' : 'bg-amber-100'
                          }`}>
                            {uploadedDocuments[doc.id || doc.name] ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <Upload className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-amber-600'}`} />
                            )}
                          </div>
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {doc.name}
                              {doc.required && <span className="text-red-500 ml-1">*</span>}
                            </p>
                            {uploadedDocuments[doc.id || doc.name] && (
                              <p className="text-xs text-green-600">{uploadedDocuments[doc.id || doc.name].name}</p>
                            )}
                          </div>
                        </div>
                        <label className={`cursor-pointer px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isDark 
                            ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' 
                            : 'bg-amber-500 text-white hover:bg-amber-600'
                        }`}>
                          {uploadedDocuments[doc.id || doc.name] ? 'Change' : 'Upload'}
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) => handleDocumentUpload(doc.id || doc.name, e.target.files[0])}
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => applicationStep > 1 ? setApplicationStep(applicationStep - 1) : setShowApplicationForm(false)}
              className={isDark ? 'border-gray-600 text-gray-300' : 'border-amber-300 text-amber-700'}
            >
              {applicationStep === 1 ? 'Cancel' : 'Previous'}
            </Button>
            
            {applicationStep < 4 ? (
              <Button
                onClick={() => setApplicationStep(applicationStep + 1)}
                className="bg-gradient-to-r from-amber-500 to-orange-600 text-white"
              >
                Next Step <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmitApplication}
                disabled={applicationSubmitting}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
              >
                {applicationSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className={`border-t py-8 ${isDark ? 'border-gray-800 bg-gray-950' : 'border-amber-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>© 2026 StudyPortal.ai • Powered by CloudLab</p>
        </div>
      </footer>
    </div>
  );
};

export default StudentPortalPage;
